import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, runTransaction, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../context/AuthContext';
import { logProductionEvent, PRODUCTION_EVENT_TYPES } from '../../utils/productionEvents';
import { useActiveTask } from '../../hooks/useActiveTask';
import { useProductionAreas, useCompanySettings, useTaskTags } from '../../hooks/useCompanyData';
import TaskTagPicker from '../../components/common/TaskTagPicker';
import TaskTagChips from '../../components/common/TaskTagChips';
import { resolveTaskTagFields, recordRecentTagIds, getRecentTagIds } from '../../utils/taskTags';
import { useTimer } from '../../hooks/useTimer';
import { useToast } from '../../components/common/Toast';
import { formatElapsed, toDatetimeLocalSeconds, fromDatetimeLocal, formatTime, elapsedSecondsBetween, SHORT_TASK_DISCARD_THRESHOLD_SECONDS } from '../../utils/time';
import { calcDurationMinutes, calcLabourMinutes, calcUnitsPerHour, calcUnitsPerLabourHour } from '../../utils/labour';
import { Square, AlertTriangle, ChevronDown, ChevronUp, Pause, Play, Plus, XCircle } from 'lucide-react';
import { useBestRunBenchmarks } from '../../hooks/useBestRunBenchmarks';
import { getBenchmarkKey } from '../../utils/benchmark';
import BenchmarkCard from '../../components/common/BenchmarkCard';
import TaskNotes from '../../components/common/TaskNotes';
import './ActiveTaskPage.css';

function TaskCard({ task, areas, settings, isLastTask, benchmarks, tags }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const toast = useToast();
  const taskBenchmark = benchmarks?.get(getBenchmarkKey(task)) ?? null;
  const tagsById = useMemo(() => new Map(tags.map((tg) => [tg.id, tg])), [tags]);

  const isPaused = !!task.pausedAt;
  const prevPausedSeconds = (task.pausedMinutes ?? 0) * 60;

  const timerElapsed = useTimer(
    task.startTime?.toDate?.() ?? task.startTime,
    !isPaused,
    prevPausedSeconds,
  );

  const frozenSeconds = useMemo(() => {
    if (!isPaused || !task.pausedAt || !task.startTime) return 0;
    const pausedAt = task.pausedAt.toDate ? task.pausedAt.toDate() : new Date(task.pausedAt);
    const startTime = task.startTime.toDate ? task.startTime.toDate() : new Date(task.startTime);
    return Math.max(0, Math.floor((pausedAt - startTime) / 1000) - prevPausedSeconds);
  }, [isPaused, task.pausedAt, task.startTime, prevPausedSeconds]);

  const elapsed = isPaused ? frozenSeconds : timerElapsed;

  const [showDetails, setShowDetails] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pausing, setPausing] = useState(false);
  const detailsFormRef = useRef(null);

  useEffect(() => {
    if (showDetails && detailsFormRef.current) {
      detailsFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showDetails]);

  const [details, setDetails] = useState({
    endTime: toDatetimeLocalSeconds(new Date()),
    quantityCompleted: '',
    unitType: '',
    lotNumber: '',
    roomId: '',
    roomName: '',
    productName: '',
    sku: '',
    downtimeMinutes: '',
    downtimeReason: '',
    notes: '',
    tagIds: task.tagIds || [],
  });
  const [errors, setErrors] = useState({});

  const warnMinutes = settings?.activeTaskWarningMinutes ?? 240;
  const isLong = elapsed > warnMinutes * 60;
  const isShortTask = elapsed < SHORT_TASK_DISCARD_THRESHOLD_SECONDS;
  const taskStartDate = task.startTime?.toDate?.() ?? (task.startTime ? new Date(task.startTime) : null);

  const set = (k, v) => setDetails((d) => ({ ...d, [k]: v }));

  const handlePause = async () => {
    if (submitting || pausing) return;
    setPausing(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.PRODUCTION_TASKS, task.id), {
        pausedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      logProductionEvent({
        companyId,
        taskId: task.id,
        eventType: PRODUCTION_EVENT_TYPES.TASK_PAUSED,
        userId: user.uid,
        task,
      });
    } catch (err) {
      toast(t('toast.saveFailed'), 'error');
      console.error(err);
    } finally {
      setPausing(false);
    }
  };

  const handleResume = async () => {
    if (submitting) return;
    try {
      const taskRef = doc(db, COLLECTIONS.PRODUCTION_TASKS, task.id);
      await runTransaction(db, async (txn) => {
        const snap = await txn.get(taskRef);
        const data = snap.data();
        if (!snap.exists() || !data.pausedAt) return; // already resumed, no-op
        const pausedAtDate = data.pausedAt.toDate ? data.pausedAt.toDate() : new Date(data.pausedAt);
        const breakMinutes = Math.max(0, Math.round((new Date() - pausedAtDate) / 1000 / 60));
        txn.update(taskRef, {
          pausedAt: null,
          pausedMinutes: (data.pausedMinutes ?? 0) + breakMinutes,
          updatedAt: serverTimestamp(),
        });
      });
      logProductionEvent({
        companyId,
        taskId: task.id,
        eventType: PRODUCTION_EVENT_TYPES.TASK_RESUMED,
        userId: user.uid,
        task: { ...task, pausedAt: null },
        durationMinutes: task.pausedAt
          ? Math.max(0, Math.round((Date.now() - (task.pausedAt.toDate?.() ?? new Date(task.pausedAt)).getTime()) / 60000))
          : null,
      });
    } catch (err) {
      toast(t('toast.saveFailed'), 'error');
      console.error(err);
    }
  };

  const handleEndTask = () => {
    const currentBreakMinutes = isPaused && task.pausedAt
      ? Math.max(0, Math.round((new Date() - (task.pausedAt.toDate?.() ?? new Date(task.pausedAt))) / 1000 / 60))
      : 0;
    const totalPausedMinutes = (task.pausedMinutes ?? 0) + currentBreakMinutes;
    setDetails((d) => ({
      ...d,
      endTime: toDatetimeLocalSeconds(new Date()),
      downtimeMinutes: totalPausedMinutes > 0 ? String(totalPausedMinutes) : d.downtimeMinutes,
    }));
    setShowDetails(true);
  };

  const handleDiscard = async () => {
    if (submitting) return;
    if (!window.confirm(t('production.discardConfirm'))) return;
    setSubmitting(true);
    try {
      const taskRef = doc(db, COLLECTIONS.PRODUCTION_TASKS, task.id);
      await runTransaction(db, async (txn) => {
        const snap = await txn.get(taskRef);
        if (!snap.exists() || snap.data().status !== 'active') {
          throw new Error('NOT_ACTIVE');
        }
        txn.update(taskRef, {
          status: 'abandoned',
          pausedAt: null,
          endTime: null,
          durationMinutes: null,
          editedBy: user.uid,
          editedAt: serverTimestamp(),
          editReason: 'Discarded by worker (short/accidental start)',
          updatedAt: serverTimestamp(),
        });
      });
      logProductionEvent({
        companyId,
        taskId: task.id,
        eventType: PRODUCTION_EVENT_TYPES.TASK_CANCELLED,
        userId: user.uid,
        task,
        reason: 'Discarded by worker (short/accidental start)',
      });
      toast(t('production.taskDiscarded'), 'success');
      if (isLastTask) navigate('/production');
    } catch (err) {
      if (err.message === 'NOT_ACTIVE') {
        toast(t('production.taskNoLongerActive'), 'warning');
      } else {
        toast(t('toast.saveFailed'), 'error');
        console.error(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!details.endTime) {
      e.endTime = t('validation.required');
    } else if (taskStartDate) {
      const end = fromDatetimeLocal(details.endTime);
      if (end.getTime() <= taskStartDate.getTime()) {
        e.endTime = isShortTask
          ? t('validation.endBeforeStartShort')
          : t('validation.endBeforeStart');
      }
    }
    if (details.quantityCompleted && Number(details.quantityCompleted) < 0)
      e.quantityCompleted = t('validation.negativeQuantity');
    if (details.downtimeMinutes && Number(details.downtimeMinutes) < 0)
      e.downtimeMinutes = t('validation.negativeDowntime');
    // Downtime cannot exceed the elapsed start→end window — that would imply
    // negative working time and zero out labour cost incorrectly.
    if (details.downtimeMinutes && details.endTime && taskStartDate) {
      const end = fromDatetimeLocal(details.endTime);
      const durationMin = elapsedSecondsBetween(taskStartDate, end) / 60;
      if (Number(details.downtimeMinutes) > durationMin)
        e.downtimeMinutes = t('validation.downtimeExceedsDuration');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const start = taskStartDate ?? new Date(task.startTime);
      const end = fromDatetimeLocal(details.endTime);
      const durationMinutes = calcDurationMinutes(start, end);

      const qty = details.quantityCompleted ? Number(details.quantityCompleted) : null;
      const dtMin = details.downtimeMinutes ? Number(details.downtimeMinutes) : null;

      // Net working time excludes downtime — keeps labour-hours consistent with
      // the admin-side cost calculation (see calcTaskMetrics).
      const netDurationMinutes = durationMinutes != null ? Math.max(0, durationMinutes - (dtMin || 0)) : null;
      const labourMinutes = calcLabourMinutes(netDurationMinutes, task.teamSize);
      const labourHours = labourMinutes != null ? parseFloat((labourMinutes / 60).toFixed(3)) : null;

      const unitsPerHour = calcUnitsPerHour(qty, durationMinutes);
      const unitsPerLabourHour = calcUnitsPerLabourHour(qty, labourMinutes);

      const roomId = details.roomId || task.roomId || null;
      const roomName = details.roomId
        ? (areas.find((a) => a.id === details.roomId)?.name ?? null)
        : (task.roomName ?? null);

      const tagFields = resolveTaskTagFields(details.tagIds, tagsById);

      const taskRef = doc(db, COLLECTIONS.PRODUCTION_TASKS, task.id);
      await runTransaction(db, async (txn) => {
        const snap = await txn.get(taskRef);
        if (!snap.exists() || snap.data().status !== 'active') {
          throw new Error('NOT_ACTIVE');
        }
        txn.update(taskRef, {
          status: 'completed',
          pausedAt: null,
          endTime: Timestamp.fromDate(end),
          durationMinutes,
          netDurationMinutes,
          labourMinutes,
          labourHours,
          unitsPerHour,
          unitsPerLabourHour,
          labourCost: null,
          labourCostCurrency: null,
          labourRateSource: 'unavailable',
          labourCostCalculatedAt: null,
          costPerUnit: null,
          quantityCompleted: qty,
          unitType: details.unitType || null,
          lotNumber: details.lotNumber.trim() || null,
          productName: details.productName.trim() || null,
          sku: details.sku.trim() || null,
          roomId,
          roomName,
          downtimeMinutes: dtMin,
          downtimeReason: details.downtimeReason.trim() || null,
          notes: details.notes.trim() || null,
          tagIds: tagFields.tagIds,
          tagNames: tagFields.tagNames,
          submittedBy: user.uid,
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      recordRecentTagIds(companyId, tagFields.tagIds);

      const completedTask = {
        ...task,
        status: 'completed',
        endTime: end,
        roomId,
        roomName,
        productName: details.productName.trim() || null,
        quantityCompleted: qty,
        netDurationMinutes,
        durationMinutes,
        downtimeMinutes: dtMin,
      };

      logProductionEvent({
        companyId,
        taskId: task.id,
        eventType: PRODUCTION_EVENT_TYPES.TASK_COMPLETED,
        userId: user.uid,
        task: completedTask,
        durationMinutes: netDurationMinutes,
        notes: details.notes.trim() || null,
        timestamp: Timestamp.fromDate(end),
        metadata: {
          totalDurationMinutes: durationMinutes,
          downtimeMinutes: dtMin,
          quantityCompleted: qty,
        },
      });

      if (roomId && task.roomId && roomId !== task.roomId) {
        logProductionEvent({
          companyId,
          taskId: task.id,
          eventType: PRODUCTION_EVENT_TYPES.ROOM_CHANGED,
          userId: user.uid,
          task: completedTask,
          previousValue: task.roomName || task.roomId,
          newValue: roomName,
          timestamp: Timestamp.fromDate(end),
        });
      }

      toast(t('production.taskSubmitted'), 'success');
      if (isLastTask) navigate('/production/recent');
    } catch (err) {
      if (err.message === 'NOT_ACTIVE') {
        toast(t('production.taskNoLongerActive'), 'warning');
      } else {
        toast(t('toast.saveFailed'), 'error');
        console.error(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const taskDisplayName = task.isCustomTaskName ? task.customTaskName : task.taskName;

  return (
    <div className="active-task-card">
      {/* ── Timer card ── */}
      <div className={`task-timer-card ${isPaused ? 'is-paused' : isLong ? 'is-warning' : ''}`}>
        <div className="task-timer-label">
          {!isPaused && <div className="live-dot-lg" />}
          <span>{isPaused ? t('production.onBreak') : t('production.taskInProgress')}</span>
        </div>
        <div className="task-timer-value">{formatElapsed(elapsed)}</div>
        <div className="task-timer-name">{taskDisplayName}</div>
        <TaskTagChips task={task} tagsById={tagsById} maxVisible={3} className="task-timer-tags" />

        <div className="task-timer-context">
          {task.roomName && <span>{task.roomName}</span>}
          {task.roomName && <span className="task-timer-dot">·</span>}
          <span>
            {task.assignmentType === 'team'
              ? t('production.team') + ' × ' + task.teamSize
              : task.employeeNames?.[0] || t('production.individual')}
          </span>
        </div>

        {!isPaused && isLong && (
          <div className="task-warning-badge">
            <AlertTriangle size={14} />
            {t('production.taskWarning')}
          </div>
        )}
      </div>

      {/* ── Action buttons ── */}
      {!showDetails && !isPaused && (
        <>
          <button className="btn btn-danger btn-lg btn-block btn-end-task" onClick={handleEndTask}>
            <Square size={20} />
            {t('production.endTask')}
          </button>
          <button className="btn btn-outline btn-lg btn-block" onClick={handlePause} disabled={pausing}>
            {pausing ? <span className="spinner" /> : <Pause size={18} />}
            {t('production.takeBreak')}
          </button>
        </>
      )}

      {!showDetails && isPaused && (
        <>
          <button className="btn btn-primary btn-lg btn-block btn-resume-task" onClick={handleResume}>
            <Play size={20} />
            {t('production.resumeTask')}
          </button>
          <button className="btn btn-ghost btn-block" onClick={handleEndTask}>
            <Square size={16} />
            {t('production.endTask')}
          </button>
        </>
      )}

      {!showDetails && isShortTask && (
        <button
          type="button"
          className="btn btn-ghost btn-block short-task-discard-btn"
          onClick={handleDiscard}
          disabled={submitting}
        >
          <XCircle size={16} />
          {t('production.discardTask')}
        </button>
      )}

      {!showDetails && (
        <TaskNotes
          taskId={task.id}
          taskCreatedBy={task.createdBy}
          task={task}
          collapsible
          compact
        />
      )}

      {/* ── Submit details form ── */}
      {showDetails && (
        <div className="task-details-form" ref={detailsFormRef}>
          <div className="task-details-header">
            <h3 className="task-details-title">{t('production.taskDetails')}</h3>
            <p className="task-details-sub">{taskDisplayName}</p>
            {task.startTime && (
              <p className="task-details-started">
                {t('production.startedAt')} {formatTime(task.startTime?.toDate?.() ?? task.startTime)}
              </p>
            )}
          </div>

          {taskBenchmark && (
            <BenchmarkCard
              benchmark={taskBenchmark}
              mode="production"
              compact
            />
          )}

          {isShortTask && (
            <div className="short-task-notice" role="status">
              <AlertTriangle size={16} />
              <div>
                <strong>{t('production.shortTaskTitle')}</strong>
                <p>{t('production.shortTaskHint')}</p>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('production.endTime')} <span className="required">*</span></label>
            <input
              type="datetime-local"
              step="1"
              className={`form-control form-control-lg ${errors.endTime ? 'is-error' : ''}`}
              value={details.endTime}
              min={taskStartDate ? toDatetimeLocalSeconds(taskStartDate) : ''}
              max={toDatetimeLocalSeconds(new Date())}
              onChange={(e) => set('endTime', e.target.value)}
            />
            {errors.endTime && <p className="form-error">{errors.endTime}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('production.downtimeMinutes')}
              <span className="form-label-optional"> ({t('common.optional')})</span>
            </label>
            {(task.pausedMinutes > 0 || isPaused) && (
              <p className="form-hint form-hint-autofill">{t('production.downtimeAutofilled')}</p>
            )}
            <input
              type="number"
              min="0"
              inputMode="numeric"
              className={`form-control form-control-lg ${errors.downtimeMinutes ? 'is-error' : ''}`}
              placeholder="0"
              value={details.downtimeMinutes}
              onChange={(e) => set('downtimeMinutes', e.target.value)}
            />
            {errors.downtimeMinutes && <p className="form-error">{errors.downtimeMinutes}</p>}
          </div>

          {Number(details.downtimeMinutes) > 0 && (
            <div className="form-group">
              <label className="form-label">
                {t('production.downtimeReason')}
                <span className="form-label-optional"> ({t('common.optional')})</span>
              </label>
              <input
                className="form-control form-control-lg"
                value={details.downtimeReason}
                onChange={(e) => set('downtimeReason', e.target.value)}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              {t('production.quantityCompleted')} <span className="form-label-optional">({t('common.optional')})</span>
            </label>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              className={`form-control form-control-lg ${errors.quantityCompleted ? 'is-error' : ''}`}
              placeholder="0"
              value={details.quantityCompleted}
              onChange={(e) => set('quantityCompleted', e.target.value)}
            />
            {errors.quantityCompleted && <p className="form-error">{errors.quantityCompleted}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('production.unitType')} <span className="form-label-optional">({t('common.optional')})</span>
            </label>
            <input
              className="form-control form-control-lg"
              placeholder={t('units.placeholder')}
              value={details.unitType}
              onChange={(e) => set('unitType', e.target.value)}
              list="unit-type-suggestions"
            />
            <datalist id="unit-type-suggestions">
              <option value={t('units.grams')} />
              <option value={t('units.kg')} />
              <option value={t('units.units')} />
              <option value={t('units.jars')} />
              <option value={t('units.bags')} />
              <option value={t('units.bottles')} />
              <option value={t('units.packages')} />
              <option value={t('units.rolls')} />
            </datalist>
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('production.lotNumber')} <span className="form-label-optional">({t('common.optional')})</span>
            </label>
            <input
              className="form-control form-control-lg"
              value={details.lotNumber}
              onChange={(e) => set('lotNumber', e.target.value)}
            />
          </div>

          {!task.roomId && areas.length > 0 && (
            <div className="form-group">
              <label className="form-label">
                {t('production.room')} <span className="form-label-optional">({t('common.optional')})</span>
              </label>
              <select
                className="form-control form-control-lg"
                value={details.roomId}
                onChange={(e) => set('roomId', e.target.value)}
              >
                <option value="">— {t('common.optional')} —</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          <button
            type="button"
            className="more-details-toggle"
            onClick={() => setShowMoreDetails((v) => !v)}
          >
            <span>{t('production.moreDetails')}</span>
            {showMoreDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showMoreDetails && (
            <>
              <div className="form-group">
                <label className="form-label">
                  {t('production.productName')} <span className="form-label-optional">({t('common.optional')})</span>
                </label>
                <input
                  className="form-control form-control-lg"
                  value={details.productName}
                  onChange={(e) => set('productName', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('production.sku')} <span className="form-label-optional">({t('common.optional')})</span>
                </label>
                <input
                  className="form-control form-control-lg"
                  value={details.sku}
                  onChange={(e) => set('sku', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('tags.taskTags')} <span className="form-label-optional">({t('common.optional')})</span>
                </label>
                <TaskTagPicker
                  tags={tags}
                  selectedIds={details.tagIds}
                  onChange={(ids) => set('tagIds', ids)}
                  suggestFromText={[details.notes, details.downtimeReason].filter(Boolean).join(' ')}
                  recentIds={getRecentTagIds(companyId)}
                  compact
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('production.notes')} <span className="form-label-optional">({t('common.optional')})</span>
                </label>
                <textarea
                  className="form-control form-control-lg"
                  placeholder={t('production.notesPlaceholder')}
                  value={details.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}

          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <span className="spinner" /> : t('production.submitTask')}
          </button>

          {isShortTask && (
            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={handleDiscard}
              disabled={submitting}
            >
              <XCircle size={16} />
              {t('production.discardTask')}
            </button>
          )}

          <button
            className="btn btn-secondary btn-block"
            onClick={() => setShowDetails(false)}
            disabled={submitting}
          >
            {t('production.keepRunning')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ActiveTaskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTasks, loading } = useActiveTask();
  const { areas } = useProductionAreas();
  const { settings } = useCompanySettings();
  const { tags } = useTaskTags();
  const { benchmarks } = useBestRunBenchmarks();

  if (loading) {
    return <div className="page-loading"><div className="spinner spinner-lg" /></div>;
  }

  if (activeTasks.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">⏱</div>
        <h3>{t('production.noActiveTask')}</h3>
        <button className="btn btn-primary btn-lg" onClick={() => navigate('/production')}>
          {t('production.startNewTask')}
        </button>
      </div>
    );
  }

  return (
    <div className="active-task-page">
      {activeTasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          areas={areas}
          settings={settings}
          isLastTask={activeTasks.length === 1}
          benchmarks={benchmarks}
          tags={tags}
        />
      ))}

      <button
        className="btn btn-outline btn-lg btn-block"
        onClick={() => navigate('/production/start-task')}
      >
        <Plus size={18} />
        {t('production.startNewTask')}
      </button>
    </div>
  );
}
